import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import authEndpoints from './endpoints/auth'
import otpEndPoint from './endpoints/otp'
import accountEndpoints from './endpoints/accounts'
import beneficiariesEndpoints from './endpoints/beneficiaries'
import transfersEndpoint from './endpoints/transfers'
import billPaymentsEndpoints from './endpoints/billpayments'
import bulkEndpoints from './endpoints/bulk'
import { refreshToken } from 'utils/AutoLogout'
import notificationEndPoint from './endpoints/profile'
import cardsEndpoints from './endpoints/cards'
import advertisementEndpoints from './endpoints/advertisement'
import bkQuickEndpoints from './endpoints/bkquick'
import loanEndpoints from './endpoints/loans'
import overdraftEndpoints from './endpoints/overdraft'
import sessionsEndpoints from './endpoints/sessions'
import { setTokenExpired, setLogoutReason } from 'slices/auth'
import openAPIEndpoints from './endpoints/openApi'
import { getApiBaseUrl } from 'config'
import { getCurrentUser, getSessionId } from 'utils/authStorage'


const DcoreUrl = getApiBaseUrl()

const baseQuery = fetchBaseQuery({
  baseUrl: DcoreUrl,
  prepareHeaders: (headers, { endpoint }) => {
    const currentUser = getCurrentUser()
    const token = currentUser?.token
    if (token) {
      if (endpoint === 'logout' || endpoint === 'keepAlive') {
        headers.set('Authorization', `Bearer ${token?.refresh_token}`)
      } else if (endpoint === 'login' || endpoint === 'forgetCredentials') {
        headers.set('Authorization', '')
      } else {
        headers.set('Authorization', `Bearer ${token?.access_token}`)
      }
    }
    if(endpoint === 'submitBulkTransactionBatch') {
      headers.set('Content-Type', 'application/x-ndjson')
    }else {
      headers.set('Content-Type', 'application/json')
    }
    headers.set('X-Channel', 'INTERNET_BANKING')
    headers.set('accept', 'application/json')
    headers.set('lang', 'ENGLISH')
    headers.set('X-Session-Id', getSessionId())
    return headers
  },
})

let refreshPromise: ReturnType<typeof refreshToken> | null = null
const refreshTokenOnce = () => {
  if (!refreshPromise) {
    refreshPromise = refreshToken().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

const baseQueryIntercep: typeof baseQuery = async (args, api, extraOptions) => {
  let queryResult: any = await baseQuery(args, api, extraOptions)
  if (queryResult.error?.status === 401) {
    const message = queryResult.error?.data?.message
    if (message === 'INVALID_SESSION') {
      api.dispatch(setLogoutReason('session_revoked'))
      api.dispatch(setTokenExpired(true))
      return queryResult
    }
    const isExpiredToken =
      typeof message === 'string' &&
      (message === 'TOKEN_EXPIRED' || message.includes('INVALID_TOKEN'))
    if (isExpiredToken) {
      const refreshResult = await refreshTokenOnce()
      if (refreshResult?.status === 200) {
        queryResult = await baseQuery(args, api, extraOptions)
      } else {
        const reason = refreshResult?.message === 'INVALID_SESSION' ? 'session_revoked' : 'inactivity'
        api.dispatch(setLogoutReason(reason))
        api.dispatch(setTokenExpired(true))
      }
    }
    if (queryResult.error?.data?.message === 'REFRESH_TOKEN_EXPIRED') {
      api.dispatch(setLogoutReason('inactivity'))
      api.dispatch(setTokenExpired(true))
    }
  }
  return queryResult
}

const appApi = createApi({
  reducerPath: 'data',
  baseQuery: baseQueryIntercep,
  tagTypes: [
    'auth',
    'transfers',
    'notifications',
    'deleteSchedulepayment',
    'favouriteBillers',
    'beneficiaries',
    'cards',
    'loanAccounts',
    'loanApplications',
    'transferHistory',
    'scheduleTransferHistory',
    'openApiTransactions',
    'authorizationsApprovals',
    'authorizationsHistory',
    'bulk-records',
    'bulk-history',
    'cardsLimit',
    'customerOverdraftSummary',
    'userSessions',
  ],
  endpoints: (builder) => ({
    ...authEndpoints(builder),
    ...otpEndPoint(builder),
    ...accountEndpoints(builder),
    ...beneficiariesEndpoints(builder),
    ...transfersEndpoint(builder),
    ...billPaymentsEndpoints(builder),
    ...bulkEndpoints(builder),
    ...notificationEndPoint(builder),
    ...cardsEndpoints(builder),
    ...advertisementEndpoints(builder),
    ...bkQuickEndpoints(builder),
    ...loanEndpoints(builder),
    ...openAPIEndpoints(builder),
    ...overdraftEndpoints(builder),
    ...sessionsEndpoints(builder),
  }),
})

export default appApi

export const {
  // Auth
  useLoginMutation,
  useVerifyOtpMutation,
  useVerifyOtpV2Mutation,
  useLogoutMutation,
  useKeepAliveQuery,
  useLazyCheckUserNameAvailabilityQuery,
  useResetCredentialsMutation,
  useSelfActivateMutation,
  useFetchSecurityQuestionsQuery,
  useSubmitSecurityQuestionsMutation,
  useChangeUsernameMutation,
  useForgetCredentialsMutation,
  useVerifyForgetCredentialsMutation,
  useLazyGetUsernamesQuery,
  useLazyGetSecurityQuestionsQuery,
  useVerifySecurityQuestionMutation,
  useSetResetCredentialsMutation,
  useFetchMigratedUserCredentialsMutation,
  useVerifyExistingCustomerMutation,
  useVerifySelfOnboardOtpMutation,
  useSaveCustomerDetailsMutation,
  useSetSafeAccessMutation,
  useVerifyCustomerUsernameMutation,
  useSolveAccountChallengeMutation,
  useVerifySelfResetOtpMutation,
  useAddNewPasswordMutation,

  //Notifications
  useSendOtpNotificationMutation,
  useLazyVerifyOtpNotificationQuery,

  //Account Endpoints
  useGetAccountBalanceMutation,
  useGetAllAccountBalancesQuery,
  useExportStatementQuery,
  useGetMiniStatementQuery,
  useLazyGetMiniStatementQuery,
  useGetAccountLimitsQuery,
  useGetAccountStatementQuery,
  useEditAccountLimitsMutation,
  useGetOverdraftDetailsQuery,
  useLazyGetOverdraftDetailsQuery,
  // Savings
  useGetCustomerSavingsAccountsQuery,
  useGetSavingsProductsVariationsQuery,
  useGetSavingsApplicationsQuery,
  useGetSavingsAccountTypesInfoMutation,
  useOpenSimpleSavingsMutation,
  useOpenTermDepositMutation,
  useOpenSavingsAccountMutation,
  useRedeemTermDepositMutation,
  useSetTargetMutation,
  useUpdateTargetMutation,
  useStopTargetMutation,
  useGetTargetDetailsQuery,

  // Loans
  useGetLoanAccountsQuery,
  useGetLoanAccountsCbsQuery,
  useGetLoanScheduleQuery,
  useLazyGetAccountInsightsQuery,
  useLazyGetLoanScheduleQuery,
  useGetLoanAccountQuery,
  useLazyGetLoanRepaymentHistoryQuery,
  useInitiateLoanRepayMutation,
  useGetLoanBalanceQuery,
  useLazyGetLoanBalanceQuery,

  //Beneficiaries Endpoints
  useGetBeneficiariesQuery,
  useAddBeneficiaryMutation,
  useDeleteBeneficiaryMutation,
  useUpdateBeneficiariesMutation,
  useLazyGetBillersFieldsQuery,
  useCheckBeneficiaryNameMutation,
  useFetchTransactionTypeQuery,
  useUpdateBeneficiaryMutation,
  useLazyValidateBeneficiaryAccountQuery,
  useValidateBeneficiaryBillpaymentAccountMutation,
  useValidateSwiftBeneficiaryCodeMutation,
  useGetBeneficaryPendingApprovalQuery,
  useApproveBeneficiaryMutation,
  useLazyImportBeneficiariesQuery,

  // Transfers
  useGetBanksQuery,
  useLazyValidateLimitQuery,
  useInitiateTransferMutation,
  useLazyValidateAccountQuery,
  useLazyGetAccountLimitQuery,
  useGetCurrenciesQuery,
  useLazyGetExchangeRateQuery,
  useLazyGetExchangeRateV2Query,
  useGetExchangeRateV2Query,
  useScheduleTransferMutation,
  useGetScheduledTransfersQuery,
  useGetTransfersHistoryQuery,
  useDeleteScheduledTransferMutation,
  useLazyValidateEkashAccountQuery,
  useInitiateEkashTransferMutation,
  useLazyGetEkashAccountsQuery,
  useGetEkashAccountsQuery,
  useRegisterEkashTransferMutation,
  useValidateAlipayTransferMutation,
  useInitiateAlipayTransferMutation,
  useInitiateSwiftTransferMutation,
  useValidateSwiftCodeMutation,
  useLazyGetPaymentPurposesQuery,
  useGetCountriesListQuery,
  useGetPartnersOnafriqQuery,
  useLazyGetBanksOnafriqQuery,
  useGetBanksOnafriqQuery,
  useInitiateTransferOnafriqMutation,
  useValidateAccountOnafriqMutation,
  useFetchOpenApipendingTransactionsQuery,
  useAuthorizePendingTransactionMutation,
  useGetOnafriqRateMutation,
  useFetchPappsBanksQuery,
  useValidatePappsAccountDetailsMutation,
  useFetchPappsTransactionRateMutation,
  useInitiatePappsTransferMutation,
  useGetStandingOrdersQuery,
  useDeleteStandingOrderMutation,
  useGetAuthorizationsPendingApprovalQuery,
  useReviewPendingApprovalMutation,
  useGetAuthorizationsHistoryQuery,
  useGetTransactionsHistoryQuery,
  useGetTransactionsApprovalHistoryQuery,
  useGetCategoryAuthorizersQuery,
  useGetApprovalProcessHistoryQuery,
  useLazyValidateNegotiatedCodeQuery,
  useGetNegotiatedCodesQuery,
  useGetFXRatesQuery,
  useGetTransactionHistoryV2Query,
  useGetBkRemitTransferPurposeQuery,
  useLazyGetOneTransferHistoryQuery,
  useLazyFetchArchivedTransfersQuery,
  useLazyValidateBalanceQuery,

  // Bill payments endpoints
  useGetBillerCategoriesQuery,
  useGetIconQuery,
  useGetFavouriteBillersQuery,
  useGetBillerFieldsQuery,
  useValidateBillIdMutation,
  useMakePaymentMutation,
  useGetPaymentHistoryQuery,
  useAddFavourateBillerMutation,
  useSchedulePaymentMutation,
  useGetScheduledPaymentsQuery,
  useDeleteSchedulePaymentMutation,
  useValidateBillerLimitMutation,
  useFetchBeneficiariesPerBillerQuery,
  useFetchBeneficiaryReferencesPerBillerQuery,
  useRequestBillPaymentProductsMutation,
  useRetryFailedNotificationMutation,
  useCheckRecentPaymentQuery,
  useLazyValidateBillerBalanceQuery,

  // Bulk
  useFetchBulkCategoriesQuery,
  useFetchBulkServicesQuery,
  useFetchBulkCustomerWashAccountsQuery,
  useCreateClientBulkMutation,
  useSubmitBulkTransactionBatchMutation,
  useFetchBulkHistoryQuery,
  useFetchBulkTransactionHistoryByIdQuery,
  useFetchBulkTransactionsQuery,
  useCompleteBulkFileMutation,
  useUpdateBulkTransactionRecordMutation,
  useAddAsingleBulkTransactionMutation,
  useDeleteASingleBulkTransactionMutation,
  useGetBulkFileStatisticsQuery,
  useLazyGetBulkFileStatisticsQuery,
  useFetchSignleBulkFileQuery,
  useDeleteMultipleBulkTransactionsMutation,
  useDeleteBulkFileMutation,
  useDownloadBulkFileDetailsQuery,
  useReValidatesBulkRecordMutation,

  // Profile
  useGetNotificationSettingQuery,
  useUpdateNotificationSettingMutation,
  useSendOtpMutation,
  useUpdatePasswordMutation,
  useGenerateTOTPMutation,
  useEnableTOTPMutation,
  useLazyGetNotificationSettingQuery,

  // Cards
  useGetCardsQuery,
  useCardMiniStatementQuery,
  useLockCardMutation,
  usePinResetRetriesMutation,
  useSetLimitMutation,
  useReloadPrepaidMutation,
  useGetCardInfoQuery,
  useLazyGetCardInfoQuery,
  useLazyGetCreditLimitInfoQuery,
  useGetCreditLimitInfoQuery,
  useGetVirtualCardsDetailsQuery,
  useCreateVirtualCardsMutation,
  useGetVirtualCardSensitiveDataQuery,
  useCheckVirtualCardCreationPermissionQuery,
  useGetVirtualCardCreationLimitQuery,

  //advertisement
  useGetAdvertisementItemsQuery,

  //BkQuick
  useGetTenureQuery,
  useCheckEligibilityMutation,
  useRegisterForBkQuickMutation,
  useApplyForBkQuickMutation,

  // Loan Origination
  useGetLoansQuery,
  useLazyGetLoansQuery,
  useInitiateLoanMutation,
  useGetLoanProfileUpdatedQuery,
  useLazyGetLoanProfileUpdatedQuery,
  useGetLoanProfileDecryptedQuery,
  useLazyGetLoanProfileDecryptedQuery,
  useGetLoansApplicationsListQuery,
  useLazyGetLoansApplicationsListQuery,
  useGetHealthQuestionsQuery,
  useAcceptLoanOfferMutation,
  useRejectLoanOfferMutation,
  useLazyViewApplicationStageQuery,
  useSaveHealthInfoMutation,
  useGetLoanContractQuery,
  useGetInsuranceContractQuery,
  useLazyGetLoanContractQuery,
  useLazyGetInsuranceContractQuery,
  useSignContractsMutation,
  useGetFileQuery,
  useLazyGetFileQuery,
  useUploadFileMutation,
  useProvideAdditionalInfoMutation,
  useAddRSSBNumberMutation,
  useCheckBKStaffQuery,
  useLazyCheckBKStaffQuery,
  useCheckLoanAllowedAmountMutation,
  useSignLoanContractMutation,
  useGiveCustomerRssbConsentMutation,
  useLazyLoadSalaryQuery,
  useLoadSalaryQuery,
  useFetchSalariesRSSBQuery,
  useLazyFetchSalariesRSSBQuery,
  useSignInsuranceContractMutation,
  useLazyDownloadDocumentQuery,
  useRejectLoanApplicationMutation,
  useDownloadLoanContractQuery,
  useLazyDownloadLoanContractQuery,
  useGetAllowedLoansQuery,
  useCheckLoanEligibilityMutation,

  // Open API
  useFetchOpenApiKeysMutation,

  // Overdraft
  useGetOverdraftTCQuery,
  useOverdraftOptInMutation,
  useOverdraftOptOutMutation,
  useOverdraftLimitChangeMutation,
  useGetCustomerOverdraftSummaryQuery,
  useGetCustomerDrawdownsQuery,
  useOverdraftDrawdownEvaluateMutation,

  // Sessions
  useGetMySessionsQuery,
  useGetSessionByIdQuery,
  useLazyGetSessionByIdQuery,
  useGetCurrentSessionQuery,
  useTerminateAllSessionsMutation,
  useTerminateOneSessionMutation,
} = appApi

